import DOMPurify from "dompurify";
import {
  X,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  ExternalLink,
  Copy,
  User,
  Mail,
  Globe,
  Timer,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  BLUEPRINT_AI_CONFIG,
  isBlueprintAIClass,
} from "@/config/calendar-config";
import type { ProcessedEvent, GeocodedEvent } from "@/types/calendar";
import {
  calculateCountdown,
  formatCountdownForContext,
  getHeatMapColor,
  shouldPulse,
} from "@/utils/countdown.utils";

interface EventDetailsSidebarProps {
  event: ProcessedEvent | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EventDetailsSidebar: React.FC<EventDetailsSidebarProps> = ({
  event,
  isOpen,
  onClose,
}) => {
  // Countdown timer state - updates every second
  const [countdown, setCountdown] = useState<ReturnType<
    typeof calculateCountdown
  > | null>(null);

  // Safely sanitize HTML content using DOMPurify
  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "b",
        "i",
        "em",
        "strong",
        "a",
        "ul",
        "ol",
        "li",
        "span",
        "div",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
      ALLOW_DATA_ATTR: false,
    });
  };

  // Process description to make links clickable and format Zoom links specially
  const processDescription = (
    description: string | null | undefined,
  ): string => {
    // Handle null, undefined, or non-string values
    if (!description || typeof description !== "string") {
      return "";
    }

    let processed = description;

    // First, detect and replace Zoom links with special formatting (before general linkification)
    const zoomRegex = /(https?:\/\/(?:[\w-]+\.)?zoom\.us\/[^\s<>"]+)/gi;
    processed = processed.replace(zoomRegex, (match) => {
      return `
        <div class="my-3 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 hover:bg-blue-100 transition-colors">
          <svg class="h-5 w-5 text-blue-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
          </svg>
          <a href="${match}" target="_blank" rel="noopener noreferrer" class="text-blue-600 font-semibold hover:text-blue-800 hover:underline">
            Join us on Zoom
          </a>
          <svg class="h-4 w-4 text-blue-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
          </svg>
        </div>
      `;
    });

    // Then, linkify other plain text URLs (that aren't already in HTML tags)
    const urlRegex = /(?<!["'>])(https?:\/\/[^\s<>"]+)(?!["'<])/gi;
    processed = processed.replace(urlRegex, (match) => {
      // Skip if this URL is already part of an anchor tag or div
      return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 hover:underline">${match}</a>`;
    });

    // Ensure all existing anchor tags have target="_blank" and proper styling
    processed = processed.replace(
      /<a\s+(?!.*target=)(?!.*class=)/gi,
      '<a target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 hover:underline" ',
    );

    return sanitizeHtml(processed);
  };

  // Update countdown every second
  useEffect(() => {
    if (!event || !isOpen) {
      setCountdown(null);
      return;
    }

    // Calculate initial countdown
    const updateCountdown = () => {
      const targetDate = (event as GeocodedEvent).nextOccurrence || event.start;
      const endDate = event.end;
      const countdownData = calculateCountdown(targetDate, endDate);
      setCountdown(countdownData);
    };

    updateCountdown();

    // Update every second for live countdown
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [event, isOpen]);
  // Helper function to check if location is a physical address
  const isPhysicalAddress = (location: string): boolean => {
    // Check for common patterns that indicate a physical address
    const addressPatterns = [
      /\d+\s+\w+/, // Has street number
      /\b(street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|way|court|ct|place|pl)\b/i,
      /\b(suite|ste|unit|apt|apartment|floor|fl|building|bldg)\b/i,
      /\b\d{5}\b/, // Has ZIP code
      /,\s*[A-Z]{2}\s+\d{5}/, // State and ZIP
      /\b(north|south|east|west|n|s|e|w)\b/i, // Directional indicators
    ];

    // Check for patterns that indicate it's NOT a physical address
    const nonAddressPatterns = [
      /^(zoom|teams|meet|skype|webex|gotomeeting)/i, // Video conferencing
      /^(online|virtual|remote|digital|web)/i, // Virtual meetings
      /^(room|conference|meeting)\s+\w+$/i, // Simple room names
      /^https?:\/\//, // URLs
      /^(tbd|tba|to be)/i, // To be determined
    ];

    // First check if it's explicitly not an address
    if (nonAddressPatterns.some((pattern) => pattern.test(location))) {
      return false;
    }

    // Then check if it matches address patterns
    return addressPatterns.some((pattern) => pattern.test(location));
  };

  // Create Google Maps embed URL without API key (free method)
  const getGoogleMapsEmbedUrl = (address: string): string => {
    // Use the free embed method that doesn't require an API key
    const encodedAddress = encodeURIComponent(address);
    return `https://maps.google.com/maps?q=${encodedAddress}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  };

  // Create Google Maps link URL
  const getGoogleMapsLinkUrl = (address: string): string => {
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDuration = (start: Date, end: Date) => {
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours === 0) {
      return `${diffMinutes}m`;
    } else if (diffMinutes === 0) {
      return `${diffHours}h`;
    } else {
      return `${diffHours}h ${diffMinutes}m`;
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log(`${type} copied to clipboard`);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getEventTypeIcon = (event: ProcessedEvent) => {
    if (event.meetingLink) return <Globe className="h-4 w-4" />;
    if (event.location) return <MapPin className="h-4 w-4" />;
    return <CalendarIcon className="h-4 w-4" />;
  };

  const getEventTypeLabel = (event: ProcessedEvent) => {
    if (event.meetingLink) return "Virtual Meeting";
    if (event.location) return "In-Person Event";
    return "Event";
  };

  const isUpcoming = (date: Date) => {
    return date.getTime() > Date.now();
  };

  const isPast = (date: Date) => {
    return date.getTime() < Date.now();
  };

  const isHappening = (start: Date, end: Date) => {
    const now = Date.now();
    return start.getTime() <= now && end.getTime() > now;
  };

  if (!isOpen || !event) {
    return null;
  }

  return (
    <div className="h-full w-full bg-white flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 p-4 md:p-6 text-white shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {getEventTypeIcon(event)}
            <span className="text-sm font-medium opacity-90">
              {getEventTypeLabel(event)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg md:text-xl font-bold leading-tight mb-2">
          {event.title}
        </h2>

        {/* Countdown Timer with Heat Map Colors */}
        {countdown &&
          !countdown.isPast &&
          (() => {
            const { color } = getHeatMapColor(countdown.timeRemaining);
            // Convert hex to RGB for opacity
            const hexToRgb = (hex: string) => {
              const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
                hex,
              );
              return result
                ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16),
                  }
                : { r: 99, g: 102, b: 241 };
            };
            const rgb = hexToRgb(color);
            const bgColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`;

            return (
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-2 ${shouldPulse(countdown.urgencyLevel) ? "animate-pulse" : ""}`}
                style={{
                  backgroundColor: bgColor,
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: `${color}80`, // 50% opacity
                }}
              >
                <Timer className="h-4 w-4" style={{ color }} />
                <div className="flex flex-col">
                  <span
                    className="text-xs font-medium opacity-90"
                    style={{ color }}
                  >
                    {countdown.isHappening ? "Happening Now" : "Starts In"}
                  </span>
                  <span
                    className="font-mono text-sm font-bold"
                    style={{ color }}
                  >
                    {formatCountdownForContext(countdown, "badge")}
                  </span>
                </div>
              </div>
            );
          })()}

        {/* Status indicator */}
        <div className="flex items-center gap-2">
          {isHappening(event.start, event.end) && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500 text-white">
              • Happening Now
            </span>
          )}
          {isUpcoming(event.start) && !isHappening(event.start, event.end) && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white">
              Upcoming
            </span>
          )}
          {isPast(event.end) && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-500 text-white">
              Past Event
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 md:p-6 md:space-y-6">
        {/* Date & Time */}
        <div className="space-y-3">
          <h3 className="flex items-center gap-2 font-semibold text-gray-900">
            <CalendarIcon className="h-5 w-5 text-indigo-600" />
            Date & Time
          </h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="font-medium text-gray-900">
              {formatDate(event.start)}
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>
                  {formatTime(event.start)} - {formatTime(event.end)}
                </span>
              </div>
              <span className="font-medium">
                ({formatDuration(event.start, event.end)})
              </span>
            </div>
          </div>
        </div>

        {/* Blueprint AI Class Signup Button */}
        {isBlueprintAIClass(event) &&
          BLUEPRINT_AI_CONFIG.enableSignup &&
          isUpcoming(event.start) && (
            <div className="space-y-3">
              <Link
                to="/blueprint-ai-signup"
                className="block w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <div className="flex items-center justify-center gap-3">
                  <Users className="h-5 w-5" />
                  <span>{BLUEPRINT_AI_CONFIG.signupButtonText}</span>
                </div>
              </Link>
              <p className="text-sm text-gray-600 text-center">
                Secure your spot in this Blueprinting AI class
              </p>
            </div>
          )}

        {/* Description */}
        {event.description && (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Description</h3>
            <div className="prose prose-sm max-w-none">
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: processDescription(event.description),
                }}
              />
            </div>
          </div>
        )}

        {/* Location */}
        {event.location && (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <MapPin className="h-5 w-5 text-indigo-600" />
              Location
            </h3>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{event.location}</span>
                  <button
                    onClick={() => copyToClipboard(event.location!, "Location")}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Copy location"
                  >
                    <Copy className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Google Maps Embed - FREE method, no API key required! */}
              {isPhysicalAddress(event.location) && (
                <div className="rounded-lg overflow-hidden shadow-md border border-gray-200">
                  <iframe
                    width="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    referrerPolicy="no-referrer-when-downgrade"
                    src={getGoogleMapsEmbedUrl(event.location)}
                    allowFullScreen
                    loading="lazy"
                    title="Event Location Map"
                    className="w-full h-[180px] md:h-[250px]"
                    aria-label={`Map showing location of ${event.location}`}
                  />
                  <div className="bg-gray-50 p-2 border-t">
                    <a
                      href={getGoogleMapsLinkUrl(event.location)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 justify-center"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View in Google Maps
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Meeting Link */}
        {event.meetingLink && (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <ExternalLink className="h-5 w-5 text-indigo-600" />
              Meeting Link
            </h3>
            <div className="space-y-3">
              <a
                href={event.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-3 px-4 rounded-lg font-medium text-center transition-all shadow-md hover:shadow-lg"
              >
                Join Meeting
              </a>

              {/* Meeting details */}
              {(event.meetingId || event.meetingPassword) && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {event.meetingId && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Meeting ID:
                      </span>
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-white rounded text-sm font-mono border">
                          {event.meetingId}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(event.meetingId!, "Meeting ID")
                          }
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          title="Copy meeting ID"
                        >
                          <Copy className="h-3 w-3 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}

                  {event.meetingPassword && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Password:
                      </span>
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-white rounded text-sm font-mono border">
                          {event.meetingPassword}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              event.meetingPassword!,
                              "Meeting Password",
                            )
                          }
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          title="Copy password"
                        >
                          <Copy className="h-3 w-3 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Attendees */}
        {event.attendees && event.attendees.length > 0 && (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <Users className="h-5 w-5 text-indigo-600" />
              Attendees ({event.attendees.length})
            </h3>
            <div className="space-y-2">
              {event.attendees.map((attendee, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {attendee.displayName}
                      </div>
                      {attendee.email && (
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {attendee.email}
                        </div>
                      )}
                      {attendee.organizer && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white mt-1 shadow-sm">
                          Organizer
                        </span>
                      )}
                    </div>
                  </div>

                  <span
                    className={`
                        inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                        ${
                          attendee.responseStatus === "accepted"
                            ? "bg-green-100 text-green-800"
                            : attendee.responseStatus === "declined"
                              ? "bg-red-100 text-red-800"
                              : attendee.responseStatus === "tentative"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                        }
                      `}
                  >
                    {attendee.responseStatus === "accepted"
                      ? "Going"
                      : attendee.responseStatus === "declined"
                        ? "Not Going"
                        : attendee.responseStatus === "tentative"
                          ? "Maybe"
                          : "No Response"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            {event.meetingLink && (
              <button
                onClick={() =>
                  copyToClipboard(event.meetingLink!, "Meeting link")
                }
                className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Copy className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Copy Link
                </span>
              </button>
            )}

            <button
              onClick={() => {
                const eventData = {
                  title: event.title,
                  start: event.start.toISOString(),
                  end: event.end.toISOString(),
                  description: event.description,
                  location: event.location,
                };
                copyToClipboard(
                  JSON.stringify(eventData, null, 2),
                  "Event details",
                );
              }}
              className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CalendarIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Copy Details
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4 md:p-6">
        <div className="flex gap-2 md:gap-3">
          {event.meetingLink && (
            <a
              href={event.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-2 px-4 rounded-lg font-medium text-center transition-all shadow-md hover:shadow-lg"
            >
              Join Now
            </a>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsSidebar;
